import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Users, ShieldOff, ShieldCheck, Ban } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';

export default function IPAlertsSection() {
  const [alerts, setAlerts] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIp, setBusyIp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/admin/ip-alerts'),
        api.get('/admin/blocked-ips'),
      ]);
      setAlerts(a.data);
      setBlocked(b.data);
    } catch {
      setAlerts([]); setBlocked([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const blockIp = async (ip) => {
    setBusyIp(ip);
    try {
      await api.post('/admin/block-ip', { ip });
      toast({ title: `IP ${ip} bloqueada` });
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Error al bloquear' });
    } finally { setBusyIp(null); }
  };

  const unblockIp = async (ip) => {
    setBusyIp(ip);
    try {
      await api.delete(`/admin/block-ip/${encodeURIComponent(ip)}`);
      toast({ title: `IP ${ip} desbloqueada` });
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Error al desbloquear' });
    } finally { setBusyIp(null); }
  };

  const blockedSet = new Set(blocked.map((b) => b.ip));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-white flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-400" /> Alertas de IP
          </h2>
          <p className="text-white/50 text-sm mt-1">
            IPs con múltiples cuentas — identifica y bloquea abusos de cupones o registros masivos.
          </p>
        </div>
        <button
          onClick={load}
          className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
          data-testid="ip-alerts-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : (
        <>
          {/* Alerts list */}
          <div>
            <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">IPs con múltiples cuentas</h3>
            {alerts.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-white/40 text-sm">Ninguna IP con múltiples cuentas.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="ip-alerts-list">
                {alerts.map((a) => {
                  const isBlocked = blockedSet.has(a.ip);
                  const busy = busyIp === a.ip;
                  return (
                    <div
                      key={a.ip}
                      className={`rounded-2xl px-5 py-4 border ${isBlocked ? 'bg-rose-500/5 border-rose-500/20' : 'bg-neutral-900 border-amber-500/20'}`}
                      data-testid={`ip-alert-${a.ip}`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isBlocked ? 'bg-rose-500/10 border border-rose-500/25' : 'bg-amber-500/10 border border-amber-500/25'}`}>
                            {isBlocked
                              ? <Ban className="w-4 h-4 text-rose-400" />
                              : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                          </div>
                          <div>
                            <p className="text-white font-mono font-bold text-sm">{a.ip}</p>
                            {a.latest && (
                              <p className="text-white/40 text-xs mt-0.5">
                                Último registro: {new Date(a.latest).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`font-black text-lg ${isBlocked ? 'text-rose-400' : 'text-amber-400'}`}>{a.count} cuentas</span>
                          <button
                            onClick={() => isBlocked ? unblockIp(a.ip) : blockIp(a.ip)}
                            disabled={busy}
                            className={`h-8 px-3 rounded-full text-xs font-bold flex items-center gap-1.5 transition ${
                              isBlocked
                                ? 'bg-white/10 text-white/70 hover:bg-emerald-500/20 hover:text-emerald-400'
                                : 'bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 border border-rose-500/30'
                            }`}
                            data-testid={`ip-${isBlocked ? 'unblock' : 'block'}-${a.ip}`}
                          >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" />
                              : isBlocked ? <><ShieldCheck className="w-3 h-3" />Desbloquear</>
                              : <><ShieldOff className="w-3 h-3" />Bloquear</>}
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(a.emails || []).map((email) => (
                          <span key={email} className="text-[11px] font-mono bg-white/5 border border-white/10 text-white/70 px-2.5 py-1 rounded-full">
                            {email}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Blocked IPs list */}
          {blocked.length > 0 && (
            <div>
              <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">IPs bloqueadas ({blocked.length})</h3>
              <div className="space-y-2" data-testid="blocked-ips-list">
                {blocked.map((b) => {
                  const busy = busyIp === b.ip;
                  return (
                    <div key={b.ip} className="bg-rose-500/5 border border-rose-500/20 rounded-xl px-4 py-3 flex items-center justify-between gap-4" data-testid={`blocked-ip-${b.ip}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Ban className="w-4 h-4 text-rose-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-white font-mono text-sm font-bold">{b.ip}</p>
                          <p className="text-white/35 text-xs truncate">
                            {b.reason} · {b.blocked_at ? new Date(b.blocked_at).toLocaleDateString('es-MX') : ''}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => unblockIp(b.ip)}
                        disabled={busy}
                        className="h-7 px-3 rounded-full text-xs font-bold bg-white/10 text-white/70 hover:bg-emerald-500/20 hover:text-emerald-400 flex items-center gap-1.5 transition flex-shrink-0"
                        data-testid={`unblock-ip-${b.ip}`}
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ShieldCheck className="w-3 h-3" />Desbloquear</>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
